import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { Cell, GridKey, LocaleInfo, Project } from '../api/types'
import { ColumnProgress } from '../components/Progress'
import type { Coverage } from '../lib/coverage'
import type { LocaleLabel } from '../lib/locales'
import { cellKey, useUi } from '../store/ui'
import { GridCell, RowDetail } from './Cell'

/** Estimate before measurement; matches a typical two-line cell so the first
 *  paint does not overlap rows before the virtualizer measures them. */
const ROW_HEIGHT = 41
const SELECT_WIDTH = 30
const KEY_WIDTH = 300
/** Fallback until the sticky header is measured. */
const HEADER_HEIGHT = 52

const count = (value: number) => value.toLocaleString('en-US')

export interface GridActions {
  save: (vars: { keyId: string; locale: string; pluralForm: string; value: string }) => void
  retranslate: (vars: { keyId: string; locale: string }) => void
  saveContext: (vars: { keyId: string; context: string }) => void
  readOnlyLocale: (code: string) => boolean
  openRename: (keyRow: GridKey) => void
  setIsPlural: (vars: { keyId: string; isPlural: boolean }) => void
  setDeprecated: (vars: { keyId: string; deprecated: boolean }) => void
}

/** The plural form shown on the collapsed row: 'other' if present, else first. */
export function summaryCell(cells: Cell[] | undefined): Cell | undefined {
  if (!cells || cells.length === 0) return undefined
  return cells.find((cell) => cell.plural_form === 'other') ?? cells[0]
}

export function EditorGrid({
  rows,
  locales,
  labels,
  project,
  actions,
  totalKeys,
  coverage,
  onClearFilters,
}: {
  rows: GridKey[]
  locales: LocaleInfo[]
  labels: Record<string, LocaleLabel>
  project: Project
  actions: GridActions
  totalKeys: number
  coverage: Record<string, Coverage>
  onClearFilters: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const cursor = useUi((state) => state.cursor)
  const setCursor = useUi((state) => state.setCursor)
  const editing = useUi((state) => state.editing)
  const setEditing = useUi((state) => state.setEditing)
  const expanded = useUi((state) => state.expanded)
  const toggleExpanded = useUi((state) => state.toggleExpanded)
  const selected = useUi((state) => state.selected)
  const toggleSelected = useUi((state) => state.toggleSelected)
  const selectMany = useUi((state) => state.selectMany)
  const openHistory = useUi((state) => state.openHistory)

  // The column header is sticky INSIDE this scroller, so the virtualizer has to
  // know both that the rows start below it (scrollMargin) and that scroll
  // targets must clear it (scrollPaddingStart) — otherwise scrollToIndex parks
  // the target row underneath the header, hiding it completely.
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT)
  useEffect(() => {
    const element = headerRef.current
    if (!element) return
    const observer = new ResizeObserver(() => setHeaderHeight(element.offsetHeight))
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const columns = useMemo<ColumnDef<GridKey>[]>(
    () => [
      { id: 'select', size: SELECT_WIDTH, header: '' },
      { id: 'key', size: KEY_WIDTH, header: 'Key', accessorKey: 'name' },
      ...locales.map<ColumnDef<GridKey>>((locale) => ({
        id: locale.code,
        header: labels[locale.code]?.short ?? locale.code,
        size: 260,
      })),
    ],
    [locales, labels],
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })
  const tableRows = table.getRowModel().rows

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 16,
    // Cache measured heights per key, not per index, so filtering does not
    // leave a row sized from whichever key previously sat at that index.
    getItemKey: (index) => tableRows[index]?.id ?? index,
    scrollMargin: headerHeight,
    scrollPaddingStart: headerHeight,
  })

  const template = `${SELECT_WIDTH}px ${KEY_WIDTH}px repeat(${locales.length}, minmax(190px, 1fr))`

  // Keep the cursor inside the current result set.
  useEffect(() => {
    const row = Math.min(cursor.row, Math.max(0, tableRows.length - 1))
    const col = Math.min(cursor.col, Math.max(0, locales.length - 1))
    if (row !== cursor.row || col !== cursor.col) setCursor({ row, col })
  }, [tableRows.length, locales.length, cursor, setCursor])

  const virtualItems = virtualizer.getVirtualItems()
  const firstRendered = virtualItems[0]?.index ?? -1

  // Move real DOM focus with the cursor (roving tabindex), scrolling it in.
  const scrolledFor = useRef('')
  useEffect(() => {
    // Before the scroller is measured the virtualizer renders nothing and every
    // scroll target it computes is guesswork (it aligns to 'end' against a
    // zero-height viewport, which is what used to park row 0 under the header).
    if (editing || virtualItems.length === 0) return
    const target = `${cursor.row}:${cursor.col}`
    const focus = () => scrollRef.current?.querySelector<HTMLElement>(`[data-cell="${target}"]`)
    const element = focus()
    if (element) {
      if (scrollRef.current?.contains(document.activeElement) || document.activeElement === document.body) {
        element.focus({ preventScroll: true })
      }
      return
    }
    // One scroll per cursor position: re-running on every render of a new row
    // window would drag the view back to the cursor while the user scrolls.
    if (scrolledFor.current === target) return
    scrolledFor.current = target
    virtualizer.scrollToIndex(cursor.row, { align: 'auto' })
    const raf = requestAnimationFrame(() => focus()?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(raf)
    // firstRendered re-runs this once the scroll has actually rendered the
    // target row, so DOM focus follows the cursor on long jumps too.
  }, [cursor, editing, virtualizer, virtualItems.length, firstRendered])

  const move = useCallback(
    (dRow: number, dCol: number) => {
      const row = Math.min(Math.max(0, cursor.row + dRow), Math.max(0, tableRows.length - 1))
      const col = Math.min(Math.max(0, cursor.col + dCol), locales.length - 1)
      setCursor({ row, col })
      virtualizer.scrollToIndex(row, { align: 'auto' })
    },
    [cursor, tableRows.length, locales.length, setCursor, virtualizer],
  )

  const startEditing = useCallback(
    (row: number, col: number) => {
      const key = tableRows[row]?.original
      const locale = locales[col]
      if (!key || !locale || actions.readOnlyLocale(locale.code)) return
      const cell = summaryCell(key.cells[locale.code])
      setEditing(cellKey(key.id, locale.code, cell?.plural_form ?? ''))
    },
    [tableRows, locales, actions, setEditing],
  )

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (editing) return
    const key = tableRows[cursor.row]?.original
    const locale = locales[cursor.col]
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1, 0)
        return
      case 'ArrowUp':
        event.preventDefault()
        move(-1, 0)
        return
      case 'ArrowRight':
        event.preventDefault()
        move(0, 1)
        return
      case 'ArrowLeft':
        event.preventDefault()
        move(0, -1)
        return
      case 'PageDown':
        event.preventDefault()
        move(20, 0)
        return
      case 'PageUp':
        event.preventDefault()
        move(-20, 0)
        return
      case 'Home':
        event.preventDefault()
        setCursor({ row: 0, col: cursor.col })
        return
      case 'End':
        event.preventDefault()
        setCursor({ row: tableRows.length - 1, col: cursor.col })
        return
      case 'Enter':
        event.preventDefault()
        startEditing(cursor.row, cursor.col)
        return
      default:
        break
    }
    if (!key) return
    if (event.key === 'x') {
      event.preventDefault()
      toggleSelected(key.id)
    } else if (event.key === 'e') {
      event.preventDefault()
      toggleExpanded(key.id)
    } else if (event.key === 'h' && locale) {
      const cell = summaryCell(key.cells[locale.code])
      if (cell?.translation_id) {
        event.preventDefault()
        openHistory({ translationId: cell.translation_id, keyName: key.name, locale: locale.code })
      }
    } else if (event.key === 'r' && locale && !locale.is_source) {
      event.preventDefault()
      actions.retranslate({ keyId: key.id, locale: locale.code })
    }
  }

  const allSelected = rows.length > 0 && rows.every((row) => selected[row.id])

  const minWidth = SELECT_WIDTH + KEY_WIDTH + 190 * locales.length

  const header = (
    <div
      ref={headerRef}
      role="row"
      className="sticky top-0 z-10 grid border-b border-ink-300 bg-ink-100 text-2xs font-semibold uppercase tracking-wider text-ink-600"
      style={{ gridTemplateColumns: template }}
    >
      <div className="flex items-center justify-center py-2" role="columnheader">
          <input
            type="checkbox"
            aria-label={allSelected ? 'Deselect all filtered keys' : 'Select all filtered keys'}
            checked={allSelected}
            onChange={() => selectMany(rows.map((row) => row.id), !allSelected)}
          />
        </div>
        <div className="flex items-center gap-2 border-l border-ink-200 px-2 py-2" role="columnheader">
          <span>Key</span>
          <span className="font-normal normal-case tracking-normal text-ink-600">
            {rows.length === totalKeys
              ? `${count(totalKeys)} keys`
              : `${count(rows.length)} of ${count(totalKeys)} keys`}
          </span>
        </div>
        {locales.map((locale) => {
          const label = labels[locale.code]
          const localeCoverage = coverage[locale.code]
          return (
            <div
              key={locale.code}
              role="columnheader"
              className="min-w-0 border-l border-ink-200 px-2 py-1.5"
            >
              <div className="flex items-center gap-1.5" title={label?.full}>
                <span className="font-mono normal-case text-ink-700">{locale.code}</span>
                {label?.variant && (
                  <span className="chip border-ink-300 bg-white normal-case tracking-normal text-ink-700">
                    {label.variant}
                  </span>
                )}
                {locale.is_source && (
                  <span className="chip border-blue-300 bg-blue-50 normal-case tracking-normal text-blue-800">
                    source
                  </span>
                )}
                {actions.readOnlyLocale(locale.code) && (
                  <span
                    className="chip border-ink-300 bg-white normal-case tracking-normal text-ink-700"
                    title="The English source is edited on the default project, not on a client project."
                  >
                    read-only
                  </span>
                )}
              </div>
              {localeCoverage && (
                <div className="font-normal normal-case tracking-normal">
                  <ColumnProgress
                    name={label?.full ?? locale.code}
                    coverage={localeCoverage}
                  />
                </div>
              )}
            </div>
          )
        })}
    </div>
  )

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-auto bg-white outline-none"
      role="grid"
      aria-rowcount={rows.length + 1}
      aria-colcount={locales.length + 2}
      aria-label={`Translation grid for ${project.name}`}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div className="relative" style={{ minWidth }}>
        {header}
        {rows.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-xs text-ink-700">
              {totalKeys === 0
                ? 'This project has no keys yet.'
                : `None of the ${count(totalKeys)} keys in this project match the current filters.`}
            </p>
            {totalKeys > 0 && (
              <button className="btn mt-2" onClick={onClearFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {/* Rendered rows stay in normal flow and the whole window is offset
                once. Absolutely positioning each row by its estimated start
                makes rows overlap until they are measured; in flow they never
                can. measureElement observes each row, so expanding one pushes
                the rows below it down without extra bookkeeping. */}
            <div
              style={{
                transform: `translateY(${(virtualItems[0]?.start ?? headerHeight) - headerHeight}px)`,
              }}
            >
              {virtualItems.map((virtualRow) => {
                const row = tableRows[virtualRow.index]
                const keyRow = row.original
                return (
                  <div key={row.id} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                    <GridRow
                      keyRow={keyRow}
                      rowIndex={virtualRow.index}
                      locales={locales}
                      labels={labels}
                      template={template}
                      cursor={cursor}
                      actions={actions}
                      isExpanded={Boolean(expanded[keyRow.id])}
                      isSelected={Boolean(selected[keyRow.id])}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// Row
// --------------------------------------------------------------------------

const GridRow = memo(function GridRow({
  keyRow,
  rowIndex,
  locales,
  labels,
  template,
  cursor,
  actions,
  isExpanded,
  isSelected,
}: {
  keyRow: GridKey
  rowIndex: number
  locales: LocaleInfo[]
  labels: Record<string, LocaleLabel>
  template: string
  cursor: { row: number; col: number }
  actions: GridActions
  isExpanded: boolean
  isSelected: boolean
}) {
  const toggleSelected = useUi((state) => state.toggleSelected)
  const toggleExpanded = useUi((state) => state.toggleExpanded)
  const isCursorRow = cursor.row === rowIndex

  return (
    <div
      className={`border-b border-ink-100 ${isSelected ? 'bg-blue-50/60' : isCursorRow ? 'bg-ink-50' : 'bg-white'} ${
        keyRow.deprecated ? 'opacity-60' : ''
      }`}
      role="row"
      aria-rowindex={rowIndex + 1}
      aria-selected={isSelected}
    >
      <div className="grid items-stretch" style={{ gridTemplateColumns: template }}>
        <div className="flex items-start justify-center pt-1.5">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelected(keyRow.id)}
            aria-label={`Select key ${keyRow.name}`}
          />
        </div>
        <div className="flex min-w-0 items-start gap-1 border-l border-ink-100 px-1.5 py-1">
          <button
            className="btn-icon h-4 w-4 shrink-0 text-2xs"
            onClick={() => toggleExpanded(keyRow.id)}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${keyRow.name}`}
          >
            <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span
                className={`truncate font-mono text-2xs text-ink-800 ${keyRow.deprecated ? 'line-through' : ''}`}
                title={keyRow.name}
              >
                {keyRow.name}
              </span>
              {keyRow.is_plural && (
                <span
                  className="chip border-indigo-300 bg-indigo-50 text-indigo-800"
                  title="Plural key — expand the row to edit every CLDR form."
                >
                  plural
                </span>
              )}
              {keyRow.deprecated && (
                <span
                  className="chip border-rose-300 bg-rose-50 text-rose-900"
                  title="Removed from every export. Translations and history are kept; restore it from the expanded row."
                >
                  deprecated
                </span>
              )}
            </div>
            {(keyRow.tags.length > 0 || keyRow.context) && (
              <div className="mt-0.5 flex items-center gap-1 overflow-hidden">
                {keyRow.tags.map((tag) => (
                  <span key={tag} className="chip border-ink-200 bg-ink-100 text-ink-600">
                    {tag}
                  </span>
                ))}
                {keyRow.context && (
                  <span
                    className="truncate text-2xs italic text-ink-600"
                    title={keyRow.context}
                  >
                    {keyRow.context}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {locales.map((locale, colIndex) => (
          <GridCell
            key={locale.code}
            keyRow={keyRow}
            locale={locale}
            cell={summaryCell(keyRow.cells[locale.code])}
            allCells={keyRow.cells[locale.code]}
            rowIndex={rowIndex}
            colIndex={colIndex}
            isCursor={isCursorRow && cursor.col === colIndex}
            actions={actions}
          />
        ))}
      </div>
      {isExpanded && (
        <RowDetail keyRow={keyRow} locales={locales} labels={labels} actions={actions} />
      )}
    </div>
  )
})

